import 'amazon-connect-streams';
import { useEffect, useRef } from 'react';
import { useEmailContext } from '../../../contexts/EmailContext';
import { extractEmailData, getCustomerDisplayName } from '../../../utils/emailContactHelpers';
import { EmailMessage } from '../../../models/Email';
import { fetchEmailThread, ThreadContact } from '../../../services/fetchEmailThread';
import { fetchEmailContent, EmailContentTransfer } from '../../../services/fetchEmailContent';
import { EMAIL_INITIATION_METHODS } from '../../../constants/email';
import { useEnrichEmailWithContent } from './useEnrichEmailWithContent';
import { useFetchNewerThreadContacts } from './useFetchNewerThreadContacts';

export function findTransferContactIds(contacts: ThreadContact[], activeContactId: string): string[] {
  const byId = new Map(contacts.map(c => [c.contactId, c]));
  let current = byId.get(activeContactId);
  const transferContactIds: string[] = [];

  while (current) {
    if (current.initiationMethod.toUpperCase() === EMAIL_INITIATION_METHODS.TRANSFER) {
      transferContactIds.push(current.contactId);
    }
    const previousId = current.previousContactId ?? current.relatedContactId;
    current = previousId ? byId.get(previousId) : undefined;
  }

  return transferContactIds;
}

export function filterThreadContacts(
  contacts: ThreadContact[],
  activeContactId: string
): { filtered: ThreadContact[]; wasTransferred: boolean; transferContactIds: string[] } {
  const previousContactIdsSet = new Set(contacts.map(c => c.previousContactId).filter(Boolean));
  const filtered = contacts.filter(c => c.contactId === activeContactId || !previousContactIdsSet.has(c.contactId));
  const transferContactIds = findTransferContactIds(contacts, activeContactId);
  return { filtered, wasTransferred: transferContactIds.length > 0, transferContactIds };
}

interface UseEmailContactHandlersProps {
  onError?: (message: string) => void;
}

export function useEmailContactHandlers({ onError }: UseEmailContactHandlersProps): void {
  const {
    updateEmailData,
    updateEmailThread,
    startLoadingThread,
    stopLoadingThread,
    setOutboundContactId,
    setSelectedEmail,
    setIsReplyOpen
  } = useEmailContext();
  const enrichEmailWithContent = useEnrichEmailWithContent();
  const fetchNewerThreadContacts = useFetchNewerThreadContacts(onError);
  const isInitializedRef = useRef(false);
  const onErrorRef = useRef(onError);
  const enrichEmailWithContentRef = useRef(enrichEmailWithContent);
  const fetchNewerThreadContactsRef = useRef(fetchNewerThreadContacts);

  onErrorRef.current = onError;
  enrichEmailWithContentRef.current = enrichEmailWithContent;
  fetchNewerThreadContactsRef.current = fetchNewerThreadContacts;

  useEffect(() => {
    if (isInitializedRef.current) {
      return;
    }
    isInitializedRef.current = true;

    // Reply drafts are separate AGENT_REPLY contacts. A reload forgets which draft belongs to which email, so we
    // remember the open ones here and put the draft id back every time that email's data is rebuilt (it replaces
    // the whole object). Once the agent sends the draft it is no longer open, and a discarded one is destroyed,
    // so neither comes back.
    const replyDraftByEmailId = new Map<string, connect.Contact>();

    const getOpenReplyDraftId = (emailId: string): string | undefined => {
      const draft = replyDraftByEmailId.get(emailId);
      return draft && draft.getState().type !== connect.ContactStateType.ENDED ? draft.getContactId() : undefined;
    };

    const handleExtractEmailData = (contact: connect.Contact): void => {
      const contactId = contact.getContactId();
      const replyDraftContactId = getOpenReplyDraftId(contactId);
      updateEmailData(contactId, {
        ...extractEmailData(contact),
        ...(replyDraftContactId && { replyDraftContactId })
      });
    };

    const handleFetchEmailThread = async (contact: connect.Contact): Promise<void> => {
      const activeContactId = contact.getContactId();
      const contactAssociationId = contact.getContactAssociationId();
      if (!contactAssociationId) return;

      try {
        startLoadingThread(activeContactId);

        const [contacts, activeContent] = await Promise.all([
          fetchEmailThread(contactAssociationId),
          fetchEmailContent(activeContactId).catch(error => {
            console.error('Failed to fetch active email content:', error);
            onErrorRef.current?.('Failed to load the email content. Please try again.');
            return undefined;
          })
        ]);

        const { filtered: filteredContacts, transferContactIds } = filterThreadContacts(contacts, activeContactId);
        const customerDisplayName = getCustomerDisplayName(contact);

        const newerContentByContactId = await fetchNewerThreadContactsRef.current(filteredContacts, activeContactId);

        const transferEntries = await Promise.all(
          transferContactIds.map(async contactId => {
            const details =
              contactId === activeContactId
                ? (activeContent?.transferInfo ?? undefined)
                : ((await fetchEmailContent(contactId).catch(() => null))?.transferInfo ?? undefined);
            return details ? ([contactId, details] as const) : undefined;
          })
        );
        const transfersByContactId = new Map(
          transferEntries.filter((entry): entry is [string, EmailContentTransfer] => !!entry)
        );

        const messages: EmailMessage[] = filteredContacts
          .map(
            ({ contactId, initiationTimestamp }): EmailMessage => ({
              contactId,
              to: [],
              cc: [],
              timestamp: new Date(initiationTimestamp).getTime()
            })
          )
          .map(message => {
            if (message.contactId === activeContactId && activeContent) {
              return enrichEmailWithContentRef.current(message, activeContent);
            }
            const newerContent = newerContentByContactId.get(message.contactId);
            return newerContent ? enrichEmailWithContentRef.current(message, newerContent) : message;
          })
          .map(message =>
            message.contactId === activeContactId && message.from && customerDisplayName
              ? { ...message, from: { ...message.from, displayName: customerDisplayName } }
              : message
          )
          .map(message => {
            const transferInfo = transfersByContactId.get(message.contactId);
            return transferInfo ? { ...message, transferInfo } : message;
          })
          .sort((a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0));

        // Each transfer's comment is accurate on its own (the backend scopes it per transfer,
        // no leaking between them), but only the most recent transfer's comment is still
        // actionable for the agent - older ones are superseded, so we hide them here.
        const lastTransferIndex = messages.reduce(
          (lastIndex, message, index) => (message.transferInfo ? index : lastIndex),
          -1
        );
        const messagesWithOnlyTheLatestComment = messages.map((message, index) =>
          message.transferInfo && index !== lastTransferIndex
            ? { ...message, transferInfo: { ...message.transferInfo, comment: '' } }
            : message
        );

        updateEmailThread(activeContactId, { contactAssociationId, messages: messagesWithOnlyTheLatestComment });
      } catch (error) {
        console.error('Failed to fetch email thread:', error);
        onErrorRef.current?.('Failed to load the email thread. Please try again.');
      } finally {
        stopLoadingThread(activeContactId);
      }
    };

    // When the page loads, Streams tells us about each open contact twice: once in connect.agent
    // and once in connect.contact. Set up twice, a contact gets two onConnected listeners and loads
    // its email twice. So we keep the ids we have set up and skip repeats. When Streams drops a
    // contact, it drops its listeners too. So we forget the id then, and the contact can be set up again.
    const handledContactIds = new Set<string>();
    const emailContactsById = new Map<string, connect.Contact>();

    // After a reload, a reply draft the agent had open is still there in Connect. Hand it back to the email it
    // replies to, so Reply reopens it rather than creating a second draft and leaving this one open forever.
    const openRestoredReply = (emailId: string): void => {
      setSelectedEmail(emailId);
      setIsReplyOpen(true);
    };

    const restoreReplyDraft = (draft: connect.Contact): void => {
      const emailId = draft.getRelatedContactId();
      if (!emailId || draft.getState().type === connect.ContactStateType.ENDED) return;

      replyDraftByEmailId.set(emailId, draft);
      draft.onDestroy(() => {
        if (replyDraftByEmailId.get(emailId) !== draft) return;
        replyDraftByEmailId.delete(emailId);
        // The email's data still names this draft; rebuild it, or Reply would think a draft is open and never start one.
        const email = emailContactsById.get(emailId);
        if (email) handleExtractEmailData(email);
      });
      // The email may not be registered yet; it opens the reply itself when it is (see handleEmailContact).
      const email = emailContactsById.get(emailId);
      if (!email) return;
      handleExtractEmailData(email);
      openRestoredReply(emailId);
    };

    const handleEmailContact = (contact: connect.Contact): void => {
      const contactId = contact.getContactId();
      if (handledContactIds.has(contactId)) return;
      handledContactIds.add(contactId);
      emailContactsById.set(contactId, contact);
      contact.onDestroy(() => {
        handledContactIds.delete(contactId);
        emailContactsById.delete(contactId);
      });

      const hasRestoredReplyDraft = getOpenReplyDraftId(contactId) !== undefined;
      handleExtractEmailData(contact);
      if (hasRestoredReplyDraft) openRestoredReply(contactId);

      contact.onConnected(() => {
        handleExtractEmailData(contact);
        void handleFetchEmailThread(contact);
      });

      // A contact in After Contact Work (e.g. the agent sent a reply, then reloaded the page) is already
      // past CONNECTED, so onConnected never fires for it. Load its thread right away or the view stays empty.
      const isEnded = contact.getState().type === connect.ContactStateType.ENDED;
      if (isEnded) {
        void handleFetchEmailThread(contact);
      }

      // The draft's contact lives in Connect, but which contact is the draft only lived in page state. After a
      // reload, take the still-open outbound contact back as the draft so the agent can finish or discard it
      // instead of leaving it open forever. A sent one waits in After Contact Work (ENDED), so it is not a draft.
      if (!isEnded && contact.getInitiationMethod()?.toUpperCase() === EMAIL_INITIATION_METHODS.OUTBOUND) {
        setOutboundContactId(contactId);
        setSelectedEmail(contactId);
        setIsReplyOpen(true);
      }
    };

    connect.agent(agent => {
      agent
        .getContacts()
        .filter(c => (c.getType() as string) === connect.ContactType.EMAIL)
        .forEach(c => {
          if (c.getInitiationMethod()?.toUpperCase() === EMAIL_INITIATION_METHODS.AGENT_REPLY) {
            restoreReplyDraft(c);
          } else {
            handleEmailContact(c);
          }
        });
    });

    connect.contact(contact => {
      if ((contact.getType() as string) !== connect.ContactType.EMAIL) {
        return;
      }

      const initiationMethod = contact.getInitiationMethod() as string;

      if (initiationMethod?.toUpperCase() === EMAIL_INITIATION_METHODS.AGENT_REPLY) {
        return;
      }

      handleEmailContact(contact);
    });
  }, [
    startLoadingThread,
    stopLoadingThread,
    updateEmailData,
    updateEmailThread,
    setOutboundContactId,
    setSelectedEmail,
    setIsReplyOpen
  ]);
}
